import React from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';
import StoryblokAssetFetcher from '../content-fetcher/StoryblokAssetFetcher';
import { parseStoryblokContent, resolveStoryblokLink } from '../../../utils/StoryblokFunctions';
import { makeDateString } from '../../../utils/dataFunctions';

import '../../../css/storyblok-custom.css';


/**
 * A recorded/upcoming session.
 *
 * Field order is fixed by agreement with the content team:
 *   Session Title
 *   Logo | Presenter Name | Presenter Title
 *   Session Summary
 *   Primary Video
 *   Related Videos
 *   Presentation Deck | Transcript
 *   Session Date | Website | Status
 */
const SessionCard = ({
    title,
    companyName,
    presentingCeo,
    status,
    summary,
    logo,
    video,
    relatedVideos,
    transcript,
    presentationDeck,
    sessionDate,
    website,
    allStories,
}) => {

    const logoFilename = (logo && logo.filename) ? logo.filename : "";
    const deckFilename = (presentationDeck && presentationDeck.filename) ? presentationDeck.filename : "";
    const transcriptFilename = (transcript && transcript.filename) ? transcript.filename : "";

    // makeDateString returns 'No Date' rather than throwing on an unparseable value
    const displayedDate = sessionDate ? makeDateString(sessionDate) : "";
    const hasDate = displayedDate && displayedDate !== 'No Date';

    const {route: websiteRoute, isExternal: websiteIsExternal} = resolveStoryblokLink(website);

    const hasFooter = hasDate || websiteRoute || status;

    return (
        <div className="panel session-card">
            {title ? <h3 className="header">{title}</h3> : null}
            {
                (logoFilename || companyName || presentingCeo) ?
                    <div className="session-card-meta">
                        {
                            logoFilename ?
                                <div className="session-card-logo">
                                    <StoryblokAssetFetcher assetType={'image'} link={logoFilename}
                                        alt={companyName ? companyName : title}
                                        height={"60"} width={"160"}/>
                                </div>
                                : null
                        }
                        <div className="session-card-heading">
                            {companyName ? <div className="session-card-company">{companyName}</div> : null}
                            {presentingCeo ? <div className="session-card-presenter">{presentingCeo}</div> : null}
                        </div>
                    </div>
                    : null
            }

            {
                summary ?
                    <div className="session-card-summary">
                        {parseStoryblokContent(summary, "", "", allStories)}
                    </div>
                    : null
            }

            {
                (video && video.length > 0) ?
                    <div className="session-card-video">
                        {parseStoryblokContent(video, "", "", allStories)}
                    </div>
                    : null
            }

            {
                (relatedVideos && relatedVideos.length > 0) ?
                    <div className="session-card-related-videos">
                        {parseStoryblokContent(relatedVideos, "", "", allStories)}
                    </div>
                    : null
            }

            {
                (deckFilename || transcriptFilename) ?
                    <div className="session-card-downloads">
                        {
                            deckFilename ?
                                <a href={deckFilename} title="Presentation Deck"
                                    target="_blank" rel="noopener noreferrer"
                                    className="btn btn-primary small">
                                    Presentation Deck
                                </a>
                                : null
                        }
                        {
                            transcriptFilename ?
                                <a href={transcriptFilename} title="Transcript"
                                    target="_blank" rel="noopener noreferrer"
                                    className="btn btn-primary small">
                                    Transcript
                                </a>
                                : null
                        }
                    </div>
                    : null
            }

            {
                hasFooter ?
                    <div className="session-card-footer">
                        {hasDate ? <span className="session-card-date">{displayedDate}</span> : null}
                        {
                            websiteRoute ?
                                (
                                    websiteIsExternal ?
                                        <a href={websiteRoute} title="Website"
                                            target="_blank" rel="noopener noreferrer"
                                            className="session-card-website">
                                            Website
                                        </a>
                                        :
                                        <Link to={websiteRoute} title="Website" className="session-card-website">
                                            Website
                                        </Link>
                                )
                                : null
                        }
                        {status ? <span className="session-card-status">{status}</span> : null}
                    </div>
                    : null
            }
        </div>
    );
};


SessionCard.propTypes = {
    /**
     * Session title
     */
    title: PropTypes.string,
    /**
     * Presenting company
     */
    companyName: PropTypes.string,
    /**
     * Name of the presenter
     */
    presentingCeo: PropTypes.string,
    /**
     * Session status, e.g. 'Recorded'
     */
    status: PropTypes.string,
    /**
     * Session summary, plain text or Storyblok rich text
     */
    summary: PropTypes.any,
    /**
     * Storyblok asset for the company logo
     */
    logo: PropTypes.any,
    /**
     * Array of 'iframe_embed' bloks holding the session recording
     */
    video: PropTypes.array,
    /**
     * Array of bloks (typically a Scrollable List of 'iframe_embed') holding
     * additional recordings. Storyblok field: RelatedVideos
     */
    relatedVideos: PropTypes.array,
    /**
     * Session date. Storyblok field: SessionDate
     */
    sessionDate: PropTypes.string,
    /**
     * Storyblok multilink to the session's website. Storyblok field: button
     */
    website: PropTypes.any,
    /**
     * Storyblok asset for the session transcript
     */
    transcript: PropTypes.any,
    /**
     * Storyblok asset for the presentation deck
     */
    presentationDeck: PropTypes.any,
    /**
     * Storyblok-specific parameter passed alongside content.
     */
    allStories: PropTypes.any,
};

SessionCard.defaultProps = {
    title: "",
    companyName: "",
    presentingCeo: "",
    status: "",
    summary: null,
    logo: null,
    video: [],
    relatedVideos: [],
    sessionDate: "",
    website: null,
    transcript: null,
    presentationDeck: null,
    allStories: {},
};

export default SessionCard;
